import { useState, useCallback, useRef } from 'react';
import { FilterRule } from '../../../types/grid';
import { getGridSettings, saveGridSettings } from '../../../utils/gridPersistence';

export const getInitialPageSize = (): number => {
  try {
    const saved = localStorage.getItem('ericius_grid_page_size');
    if (saved) {
      const parsed = parseInt(saved, 10);
      if ([25, 50, 100, 250].includes(parsed)) {
        return parsed;
      }
    }
  } catch (e) {
    // localStorage may be inaccessible in private mode
  }
  return 25;
};

export function useTableGridQuery(targetTableId?: string) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(getInitialPageSize);
  const [serverSearch, setServerSearch] = useState('');
  const [serverIdFilter, setServerIdFilter] = useState('');
  const [unfilteredTotal, setUnfilteredTotal] = useState<number | null>(null);

  const [sortCol, setSortCol] = useState<string>(() => {
    if (targetTableId) {
      const saved = getGridSettings(`table_${targetTableId}`);
      if (saved?.sortCol) return saved.sortCol;
    }
    return 'created_at';
  });

  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(() => {
    if (targetTableId) {
      const saved = getGridSettings(`table_${targetTableId}`);
      if (saved?.sortDir) return saved.sortDir;
    }
    return 'desc';
  });

  const [sortRules, setSortRules] = useState<Array<{ id: string; desc: boolean }>>(() => {
    if (targetTableId) {
      const saved = getGridSettings(`table_${targetTableId}`);
      if (saved?.sortRules && saved.sortRules.length > 0) return saved.sortRules;
      if (saved?.sortCol) return [{ id: saved.sortCol, desc: saved.sortDir === 'desc' }];
    }
    return [];
  });

  const [filterRules, setFilterRules] = useState<FilterRule[]>(() => {
    if (targetTableId) {
      const saved = getGridSettings(`table_${targetTableId}`);
      if (saved?.filterRules) return saved.filterRules;
    }
    return [];
  });

  const handleSortChange = useCallback(
    (newSort: string, newDir: 'asc' | 'desc', allSorts?: Array<{ id: string; desc: boolean }>) => {
      setSortCol((prev) => (prev === newSort ? prev : newSort));
      setSortDir((prev) => (prev === newDir ? prev : newDir));
      let nextRules: Array<{ id: string; desc: boolean }> = [];
      if (allSorts && allSorts.length > 0) {
        nextRules = allSorts;
      } else if (newSort) {
        nextRules = [{ id: newSort, desc: newDir === 'desc' }];
      }
      setSortRules((prev) => (JSON.stringify(prev) === JSON.stringify(nextRules) ? prev : nextRules));
      setPage(1);

      if (targetTableId) {
        saveGridSettings(`table_${targetTableId}`, {
          sortCol: newSort,
          sortDir: newDir,
          sortRules: nextRules,
        });
      }
    },
    [targetTableId]
  );

  const handleFilterChange = useCallback(
    (newRules: FilterRule[]) => {
      setFilterRules((prev) => (JSON.stringify(prev) === JSON.stringify(newRules) ? prev : newRules));
      setPage(1);

      if (targetTableId) {
        saveGridSettings(`table_${targetTableId}`, {
          filterRules: newRules,
        });
      }
    },
    [targetTableId]
  );

  const handleServerSearchChange = useCallback((search: string, idFilter?: string) => {
    const nextId = idFilter || '';
    setServerSearch((prevSearch) => {
      if (prevSearch !== search) {
        setPage(1);
      }
      return search;
    });
    setServerIdFilter((prevId) => {
      if (prevId !== nextId) {
        setPage(1);
      }
      return nextId;
    });
  }, []);

  const isEditingRef = useRef(false);
  const handleEditingChange = useCallback((isEditing: boolean) => {
    isEditingRef.current = isEditing;
  }, []);

  const handlePageSizeChange = useCallback((newSize: number) => {
    setPageSize(newSize);
    setPage(1);
    try {
      localStorage.setItem('ericius_grid_page_size', String(newSize));
    } catch (e) {
      // ignore
    }
  }, []);

  return {
    page,
    setPage,
    pageSize,
    setPageSize,
    handlePageSizeChange,
    serverSearch,
    setServerSearch,
    serverIdFilter,
    setServerIdFilter,
    handleServerSearchChange,
    sortCol,
    setSortCol,
    sortDir,
    setSortDir,
    sortRules,
    setSortRules,
    handleSortChange,
    filterRules,
    setFilterRules,
    handleFilterChange,
    unfilteredTotal,
    setUnfilteredTotal,
    isEditingRef,
    handleEditingChange,
  };
}
